import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogoLensa } from './LogoLensa';
import { Avatar } from './Avatar';
import { ThemeToggle } from './ThemeToggle';
import { isAdmin, isStaff, useAuth } from '../hooks/useAuth';
import { LanguageToggle } from './LanguageToggle';
import { useBahasa } from '../i18n';
import { api } from '../lib/api';

const STORAGE_KEY = 'lensa.sidebar';

/**
 * Ikut gaya ikon yang sudah dipakai `ThemeToggle`: 24-unit viewBox, stroke
 * 1,75, ujung membulat. Bobot stroke yang berbeda-beda antar ikon terlihat
 * seperti kesalahan cetak, bukan seperti variasi.
 */
const icon = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Ikon §G9, berurutan: grid, kamera, film, daftar/peringatan, orang, roda gigi. */
const ICONS = {
  grid: (
    <svg {...icon}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  camera: (
    <svg {...icon}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h9A1.5 1.5 0 0 1 15 7.5v9A1.5 1.5 0 0 1 13.5 18h-9A1.5 1.5 0 0 1 3 16.5z" />
      <path d="m15 10.5 4.4-2.6a.75.75 0 0 1 1.1.65v6.9a.75.75 0 0 1-1.1.65L15 13.5z" />
    </svg>
  ),
  film: (
    <svg {...icon}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 4v16M17 4v16M3 12h18M3 8h4M3 16h4M17 8h4M17 16h4" />
    </svg>
  ),
  // Panah unduh (§G12). Bentuknya sengaja berbeda dari segitiga play yang
  // dipakai ikon tile menuju Playback (§G9): yang satu membuka halaman, yang
  // satu mengambil berkas, dan ikon yang sama untuk keduanya menjanjikan berkas
  // yang tidak pernah datang.
  download: (
    <svg {...icon}>
      <path d="M12 4v11" />
      <path d="M8 11l4 4 4-4" />
      <path d="M5 19h14" />
    </svg>
  ),
  list: (
    <svg {...icon}>
      <path d="M4 6h16M4 12h16M4 18h10" />
      <path d="M19.5 15v2.5" />
      <path d="M19.5 20.5h.01" />
    </svg>
  ),
  user: (
    <svg {...icon}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  ),
  gear: (
    // Gigi digambar sebagai rusuk pendek yang menyentuh lingkaran luar. Versi
    // sebelumnya memakai garis yang menggantung jauh dari badannya dan terbaca
    // sebagai matahari, bukan roda gigi.
    <svg {...icon}>
      <circle cx="12" cy="12" r="3.25" />
      <path d="M12 2.75v2.4M12 18.85v2.4M21.25 12h-2.4M5.15 12h-2.4M18.54 5.46l-1.7 1.7M7.16 16.84l-1.7 1.7M18.54 18.54l-1.7-1.7M7.16 7.16l-1.7-1.7" />
      <circle cx="12" cy="12" r="8" />
    </svg>
  ),
  pulse: (
    <svg {...icon}>
      <path d="M3 12h3.5l2-5.5 3.5 11 2.5-5.5H21" />
    </svg>
  ),
  // Grid dengan satu sel ditandai — "kumpulan tile yang disusun", dibedakan
  // dari ikon `grid` polos milik Dashboard supaya dua baris menu tidak terbaca
  // sebagai tautan yang sama.
  layout: (
    <svg {...icon}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 9v12" />
    </svg>
  ),
  menu: (
    <svg {...icon}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  close: (
    <svg {...icon}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  ),
  chevron: (
    <svg {...icon}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  ),
  /** About (§G15) — "i" dalam lingkaran, ikon paling universal untuk keterangan. */
  info: (
    <svg {...icon}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  ),
  /** Custom Monitors (§F9) — lembaran bertumpuk: banyak susunan, satu kumpulan. */
  layers: (
    <svg {...icon}>
      <path d="m12 3 9 5-9 5-9-5z" />
      <path d="m3 13 9 5 9-5" />
    </svg>
  ),
} as const;

type NavItem = {
  to: string;
  label: string;
  icon: keyof typeof ICONS;
  end?: boolean;
  badge?: boolean;
};

/** Urutan persis §G9. */
const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: 'grid', end: true },
  // Custom Monitors (§F9) duduk tepat di bawah Dashboard karena itu memang
  // hubungannya: dashboard yang diberikan admin, lalu susunan sendiri yang
  // dirakit dari isinya. Terbuka untuk semua peran — viewer justru yang paling
  // memerlukannya.
  { to: '/monitors', label: 'Custom Dashboards', icon: 'layers' },
  { to: '/cameras', label: 'Cameras', icon: 'camera' },
  { to: '/videos', label: 'Recordings', icon: 'film' },
  // Download Center (§G12) tepat di bawah Video — di situlah export dibuat, dan
  // di situ pula orang mencari hasilnya.
  { to: '/downloads', label: 'Downloads', icon: 'download' },
];

/**
 * §G9: menu pengelolaan dipisah garis dan disembunyikan SEPENUHNYA dari Viewer.
 *
 * Users dan Dashboard List terbuka untuk **operator** juga (§F8) — menugaskan
 * kamera ke orang justru pekerjaannya, dan halaman yang menyusun tidak bisa
 * dipakai kalau tidak bisa dibuka. Yang membedakan operator dari admin bukan
 * halamannya melainkan tombol di dalamnya.
 */
const NAV_STAFF: NavItem[] = [
  /**
   * Logs pindah ke sini 8 September 2026, atas permintaan lapangan: *"di sisi
   * user ga perlu ada side nav Logs, ilang aja kalo user."*
   *
   * Ia dulu terbuka untuk semua peran dengan alasan yang masih benar (§D7.3:
   * yang disaring per assignment adalah **isinya**, bukan aksesnya). Yang
   * berubah bukan alasan itu melainkan siapa yang membaca layar ini: pemakai
   * biasa membuka Lensa untuk melihat kamera, dan menu yang isinya `stderr`
   * ffmpeg cuma menambah satu pintu yang tidak pernah ia butuhkan.
   *
   * Badge titik merahnya ikut pindah — ia menghitung kamera bermasalah, dan
   * yang berbuat sesuatu atas kamera bermasalah memang staf.
   */
  { to: '/logs', label: 'Logs', icon: 'list', badge: true },
  { to: '/users', label: 'Users', icon: 'user' },
  { to: '/dashboards', label: 'Camera Access', icon: 'layout' },
];

/** Setting sistem — admin saja: root rekaman, retensi, kapasitas. */
const NAV_ADMIN: NavItem[] = [{ to: '/admin', label: 'Settings', icon: 'gear' }];

/** Diagnostik sistem — admin saja, dipisah dari menu admin lain di kaki daftar. */
const NAV_BAWAH: NavItem[] = [{ to: '/status', label: 'Status', icon: 'pulse' }];

/**
 * About (§G15) — semua peran, di baris paling bawah.
 *
 * Duduk di kelompok yang sama dengan Status karena keduanya sama-sama "tentang
 * sistemnya, bukan tentang kamera" — tapi tanpa gerbang peran: yang ada di
 * dalamnya nama produk dan pemiliknya, dan itu bukan diagnostik.
 */
const NAV_TENTANG: NavItem[] = [{ to: '/about', label: 'About', icon: 'info' }];

/**
 * Badge jumlah kamera yang **sedang** bermasalah (§G9).
 *
 * Bukan jumlah kamera yang sempat error dalam 24 jam — itu angka panel di atas
 * tabel `/logs` (§D7.3), dan badge memang tidak memakainya. Titik merah di
 * navigasi dibaca orang sebagai "ada yang rusak sekarang"; kalau diisi riwayat,
 * ia menyala sehari penuh untuk kamera yang pulih dalam hitungan menit dan
 * tidak ada cara memadamkannya. Badge ini padam sendiri saat kameranya
 * tersambung lagi — tidak ada status "sudah dibaca" (§D7.3).
 *
 * Di-poll 60 detik, bukan realtime — §G9 menyebut itu eksplisit karena tidak
 * sepadan biayanya. Badge hanya muncul kalau > 0: badge "0" yang selalu
 * menempel melatih orang untuk mengabaikannya.
 */
function useErrorBadge(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let hidup = true;
    const ambil = () =>
      void api<{ now: { cameraCount: number } }>('/logs/summary')
        .then((s) => hidup && setCount(s.now.cameraCount))
        .catch(() => undefined);
    ambil();
    const t = window.setInterval(ambil, 60_000);
    return () => {
      hidup = false;
      window.clearInterval(t);
    };
  }, []);

  return count;
}

/**
 * Label peran untuk mata manusia (§F8). `viewer` disebut "User" karena itu yang
 * dipakai orang saat bicara; nilai mentahnya tetap di DB supaya 43 akun hasil
 * import tidak perlu ditulis ulang.
 */
const LABEL_PERAN = {
  admin: 'Administrator',
  operator: 'Operator',
  viewer: 'User',
} as const;

/**
 * Kaki sidebar: tema + akun (§G9, 23 Agustus 2026).
 *
 * Dulu keduanya duduk di bilah atas. Bilah itu dibongkar atas keluhan lapangan
 * yang tepat sasaran: satu-satunya isinya adalah toggle tema dan nama orang,
 * dan keduanya membeli ±61px tinggi di SETIAP halaman — termasuk /dashboard,
 * yang seluruh alasan keberadaannya adalah gambar kamera (§G7 no.1). "Bagian
 * atasnya buang-buang frame, kamera jadi turun gara-gara blok putih di atas."
 *
 * Pojok kiri bawah dipilih pelapornya sendiri, dan itu juga tempat yang sama
 * dengan aplikasi lain yang mereka pakai — jadi tidak ada yang perlu dicari.
 *
 * Menunya membuka ke ATAS (`bottom-full`): dari baris paling bawah layar, menu
 * yang membuka ke bawah akan tergunting tepi jendela.
 */
function KakiAkun({ ciut }: { ciut: boolean }) {
  const { user, impersonator, logout, setViewMode } = useAuth();
  const { t } = useBahasa();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Disclosure biasa, bukan `role="menu"`: pola menu ARIA menuntut navigasi
  // panah dan roving tabindex, sementara isinya di sini tiga tautan. Yang wajib
  // tetap dipenuhi — Esc menutup, fokus kembali ke pemicunya.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  if (!user) return null;

  return (
    <div
      ref={wrapRef}
      className={`relative mt-auto flex flex-col border-t border-line p-2 ${
        ciut ? 'items-center gap-1' : 'gap-0.5'
      }`}
    >
      {/* Dua baris, bukan satu: pada rail 224px, tombol tema di sebelah nama
          menyisakan ±112px untuk nama orang — "Administrator Lensa" terpotong
          jadi "Administrat…". Nama orang adalah alasan blok ini ada. */}
      {/* Bahasa tepat di atas tema: keduanya "cara aplikasi ini tampil buat
          saya", dan keduanya diminta lapangan supaya ada di menu kiri. */}
      <LanguageToggle bentuk={ciut ? 'ikon' : 'baris'} />
      <ThemeToggle bentuk={ciut ? 'ikon' : 'baris'} />

      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={ciut ? user.fullName : undefined}
        className={`flex min-h-tap items-center gap-3 rounded-md text-sm transition-colors hover:bg-subtle ${
          ciut ? 'min-w-tap justify-center' : 'w-full min-w-0 px-2'
        }`}
      >
        <Avatar
          userId={user.id}
          fullName={user.fullName}
          hasAvatar={user.hasAvatar}
          versi={user.avatarVersion}
          size="h-7 w-7 text-xs"
        />
        {!ciut && (
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate">{user.fullName}</span>
            <span className="block truncate text-xs text-muted">
              {t(LABEL_PERAN[user.role])}
              {user.viewAsUser && ` · ${t('User view')}`}
            </span>
          </span>
        )}
        <span className="sr-only">{t('— open account menu')}</span>
      </button>

      {open && (
        <div className="absolute bottom-full left-2 z-50 mb-1 w-56 rounded-md border border-line bg-surface p-2 shadow-lg">
          <p className="px-2 py-1 text-sm font-semibold">{user.fullName}</p>
          <p className="px-2 pb-2 text-xs text-muted">
            {user.username} · {t(LABEL_PERAN[user.role])}
            {user.viewAsUser && ` · ${t('User view')}`}
          </p>
          {impersonator && (
            <p className="px-2 pb-2 text-xs text-amber-600">
              {t('Signed in as {username}', { username: impersonator.username })}
            </p>
          )}
          <hr className="my-1 border-line" />
          <NavLink
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex min-h-tap w-full items-center rounded-md px-2 text-sm transition-colors hover:bg-subtle"
          >
            {t('Profile')}
          </NavLink>
          {/* Masuk ke User view (§F8.1). Ditawarkan hanya kepada yang punya
              sesuatu untuk dilepas — viewer sudah berada di sana. */}
          {user.role !== 'viewer' && !user.viewAsUser && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void setViewMode(true);
              }}
              className="flex min-h-tap w-full items-center rounded-md px-2 text-sm transition-colors hover:bg-subtle"
            >
              {t('Switch to User view')}
            </button>
          )}
          <button
            type="button"
            onClick={() => void logout()}
            className="flex min-h-tap w-full items-center rounded-md px-2 text-sm transition-colors hover:bg-subtle"
          >
            {t('Sign out')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Jalan keluar dari User view (§F8.1), di kaki sidebar tepat di atas akun.
 *
 * Sejak bilah atas dibongkar, ini satu-satunya tempat yang selalu terlihat di
 * desktop tanpa membuka apa pun — dan mode ini memang harus mustahil
 * dilupakan. Amber, sama dengan banner sesi pinjaman: keadaan yang disengaja,
 * bukan kegagalan.
 */
function KeluarUserView({ ciut }: { ciut: boolean }) {
  const { user, setViewMode } = useAuth();
  const { t } = useBahasa();
  if (!user?.viewAsUser) return null;

  return (
    <div className={ciut ? 'px-2 pt-2' : 'px-2 pt-2'}>
      <button
        type="button"
        onClick={() => void setViewMode(false)}
        title={ciut ? t('Back to Admin view') : undefined}
        className={`flex min-h-tap w-full items-center gap-1.5 rounded-md bg-amber-500 text-sm font-semibold text-black transition-colors hover:bg-amber-400 ${
          ciut ? 'justify-center' : 'px-3'
        }`}
      >
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M11 5 4 12l7 7M4 12h16" />
        </svg>
        <span className={ciut ? 'sr-only' : 'truncate'}>{t('Back to Admin view')}</span>
      </button>
    </div>
  );
}

function bacaCollapsed(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'collapsed') return true;
    if (v === 'expanded') return false;
  } catch {
    /* penyimpanan diblokir — pakai default di bawah */
  }
  // Default mengikuti ruang yang ada, bukan selera: di layar sempit sidebar
  // penuh memakan lebar yang dibutuhkan tile video (§G7 poin 1).
  return window.matchMedia('(max-width: 1024px)').matches;
}

/**
 * Navigasi §G9.
 *
 * Dua wujud, satu isi:
 *   ≥1024px  rail tetap di kiri, bisa diciutkan jadi ikon saja.
 *   <1024px  laci (drawer) yang menutupi konten dan dipanggil dari tombol
 *            hamburger di header.
 *
 * Rail permanen di layar 390px memakan 224 dari 390 piksel — konten tersisa
 * ±166px dan setiap kartu jadi kolom satu kata. Karena itu di mobile rail-nya
 * tidak diciutkan, tapi dikeluarkan dari alur sama sekali.
 */
export function AppSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const { t } = useBahasa();
  const errorCameras = useErrorBadge();
  const [collapsed, setCollapsed] = useState(bacaCollapsed);
  const location = useLocation();
  const laciRef = useRef<HTMLElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? 'collapsed' : 'expanded');
    } catch {
      /* penyimpanan diblokir — pilihan tetap berlaku untuk sesi ini */
    }
  }, [collapsed]);

  // Pindah halaman menutup laci. Tanpa ini laci tetap menutupi halaman yang
  // baru saja dibuka — dan itu justru terasa seperti aplikasi macet.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') tutupDanKembalikanFokus();
    };
    document.addEventListener('keydown', onKey);

    // Fokus masuk ke laci saat dibuka; tanpa ini pemakai keyboard membuka laci
    // lalu tab-nya masih berjalan di balik overlay.
    laciRef.current?.focus();

    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function tutupDanKembalikanFokus() {
    onClose();
    // Pemicunya ada di `AppHeader`; id lebih murah daripada mengoper ref lewat
    // dua komponen hanya untuk satu pemanggilan focus().
    document.getElementById('nav-toggle')?.focus();
  }

  const item = (n: NavItem, ciut: boolean) => (
    <li key={n.to}>
      <NavLink
        to={n.to}
        end={n.end}
        // Nama tetap terbaca screen reader saat menyempit (label hanya
        // di-`sr-only`, tidak dibuang), dan `title` memberi tooltip untuk mouse.
        title={ciut ? t(n.label) : undefined}
        className={({ isActive }) =>
          `relative flex min-h-tap items-center gap-3 rounded-md px-3 text-sm transition-colors ${
            ciut ? 'justify-center' : ''
          } ${isActive ? 'bg-subtle font-semibold text-accent-text' : 'hover:bg-subtle'}`
        }
      >
        {({ isActive }) => (
          <>
            {/* Aktif tidak ditandai warna saja (§G3.1 pola yang sama): ada pita
                di tepi kiri yang tetap terlihat di mode kontras paksa. */}
            <span
              aria-hidden="true"
              className={`absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r ${
                isActive ? 'bg-accent-text' : 'bg-transparent'
              }`}
            />
            <span className="shrink-0">{ICONS[n.icon]}</span>
            <span className={ciut ? 'sr-only' : 'truncate'}>{t(n.label)}</span>

            {n.badge && errorCameras > 0 && (
              <span
                className={`rounded-full bg-red-600 px-1.5 text-xs font-bold leading-5 text-white ${
                  ciut ? 'absolute right-1 top-1 px-1' : 'ml-auto'
                }`}
              >
                {errorCameras}
                <span className="sr-only">{t(' cameras need attention')}</span>
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );

  const daftar = (ciut: boolean) => (
    <nav aria-label={t('Main navigation')} className="flex-1 overflow-y-auto px-2">
      <ul className="flex flex-col gap-1">{NAV.map((n) => item(n, ciut))}</ul>

      {/* Dalam User view (§F8.1) seluruh blok ini hilang, bukan di-disable:
          modenya dimaksudkan untuk melihat apa yang dilihat pemakai biasa, dan
          pemakai biasa tidak pernah melihat menu ini sama sekali (§G9).
          `isStaff`/`isAdmin` sudah memperhitungkan modenya. */}
      {isStaff(user) && (
        <>
          <hr className="my-2 border-line" />
          <ul className="flex flex-col gap-1">{NAV_STAFF.map((n) => item(n, ciut))}</ul>
        </>
      )}

      {isAdmin(user) && (
        <ul className="mt-1 flex flex-col gap-1">{NAV_ADMIN.map((n) => item(n, ciut))}</ul>
      )}

      {/* Kelompok kaki daftar. Garisnya selalu ada karena About selalu ada;
          Status menyusul di atasnya hanya untuk admin. */}
      <hr className="my-2 border-line" />
      <ul className="flex flex-col gap-1">
        {isAdmin(user) && NAV_BAWAH.map((n) => item(n, ciut))}
        {NAV_TENTANG.map((n) => item(n, ciut))}
      </ul>
    </nav>
  );

  /**
   * Chip putih di belakang logo dulu penambal karena aset mode gelap belum ada
   * (§H4 poin 4). Asetnya sekarang ada, jadi penambalnya dicabut — logo duduk
   * langsung di atas permukaan sidebar, seperti maunya §G5.
   *
   * Rail ciut selebar 64px menyisakan 48px setelah padding, dan varian `kecil`
   * memuat wordmark di bawah mark-nya. Di bawah ~40px wordmark itu berhenti
   * terbaca dan cuma jadi noda, jadi tingginya dipakai sebanyak yang muat —
   * bukan disamakan dengan logo lebar yang wordmark-nya sejajar mark.
   */
  const logo = (ciut: boolean) => (
    <NavLink to="/dashboard" className="flex items-center rounded-md">
      {/* `alt` dikosongkan: nama aksesibelnya datang dari <span> di bawah. */}
      <LogoLensa varian={ciut ? 'kecil' : 'lebar'} tinggi={ciut ? 'h-10 w-auto' : 'h-7 w-auto'} />
      <span className="sr-only">{t('Lensa — home')}</span>
    </NavLink>
  );

  const jam = <div className="lensa-sidebar-clock px-5 pb-3 text-muted"><span className="sandya-clock" data-sandya-clock="" data-status="waiting" title="ntp.sandya.net"><span className="sandya-clock-date" data-clock-date="">—</span><i className="sandya-clock-dot" aria-hidden="true" /><time data-clock-time="">--:--:--.--- WIB</time></span></div>;

  return (
    <>
      {/* — Rail desktop — */}
      <aside
        data-lensa-sidebar-collapsed={collapsed}
        className={`hidden shrink-0 flex-col border-r border-line bg-surface transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex ${
          collapsed ? 'w-16' : 'w-56'
        }`}
      >
        {/* Tombol ciut duduk di baris logo, pojok kanan atas — tempat yang sama
            dengan aplikasi web lain, dan tempat orang pertama kali mencarinya.
            Sebelumnya ia di dasar rail, di bawah daftar navigasi: pada layar
            pendek posisi itu jatuh di luar lipatan dan praktis tak terlihat.

            Saat rail ciut, baris ini jadi dua tingkat — logo di atas, tombol di
            bawahnya — karena 48px yang tersisa tidak muat memuat keduanya
            bersebelahan tanpa keduanya jadi terlalu kecil untuk disentuh. */}
        <div
          className={`flex py-3 ${
            collapsed ? 'flex-col items-center gap-1 px-2' : 'items-center gap-2 px-3'
          }`}
        >
          {logo(collapsed)}

          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('Expand navigation') : t('Collapse navigation')}
            title={collapsed ? t('Expand navigation') : t('Collapse navigation')}
            className={`flex min-h-tap min-w-tap items-center justify-center rounded-md text-muted transition-colors hover:bg-subtle ${
              collapsed ? '' : 'ml-auto'
            }`}
          >
            {/* Anak panah menunjuk ke arah yang akan dituju: ke kiri saat rail
                terbuka (akan menyempit), ke kanan saat sudah ciut. */}
            <span
              className={`transition-transform duration-200 ease-out motion-reduce:transition-none ${
                collapsed ? '' : 'rotate-180'
              }`}
            >
              {ICONS.chevron}
            </span>
          </button>
        </div>

        {!collapsed && jam}
        {daftar(collapsed)}
        <KeluarUserView ciut={collapsed} />
        <KakiAkun ciut={collapsed} />
      </aside>

      {/* — Laci mobile — */}
      <div className={`lg:hidden ${open ? '' : 'pointer-events-none'}`}>
        <div
          onClick={tutupDanKembalikanFokus}
          aria-hidden="true"
          className={`fixed inset-0 z-30 bg-black/50 transition-opacity duration-200 motion-reduce:transition-none ${
            open ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <aside
          ref={laciRef}
          tabIndex={-1}
          aria-label={t('Main navigation')}
          // `inert` menahan fokus di luar laci yang tertutup — kalau tidak, Tab
          // menyusuri enam tautan tak terlihat sebelum sampai ke konten.
          {...(open ? {} : { inert: '' })}
          className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line bg-surface shadow-xl transition-transform duration-200 ease-out motion-reduce:transition-none ${
            open ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-3 py-3">
            {logo(false)}
            <button
              type="button"
              onClick={tutupDanKembalikanFokus}
              aria-label={t('Close navigation')}
              className="flex min-h-tap min-w-tap items-center justify-center rounded-md text-muted transition-colors hover:bg-subtle"
            >
              {ICONS.close}
            </button>
          </div>

          {jam}
          {daftar(false)}
          <KeluarUserView ciut={false} />
          <KakiAkun ciut={false} />
        </aside>
      </div>
    </>
  );
}
